<template>
  <div class="general-settings">
    <v-form ref="generalForm" v-model="formValid">
      <!-- Website Settings Section -->
      <v-card class="mb-6">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-web</v-icon>
          <span class="headline">Website Settings</span>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Page Title</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <TextField
                v-model="websiteSettings.pageTitle"
                :label="'Page Title'"
                :rules="[
                  validateRules.required(
                    websiteSettings.pageTitle,
                    'Page Title'
                  ),
                ]"
                placeholder="Enter Page Title"
                dense
                filled
                solo
                flat
                class="pt-0 mt-3"
                @input="$emit('settings-changed')"
              />
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500"
                >Page Description</label
              >
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <TextAreaField
                v-model="websiteSettings.pageDescription"
                :label="'Page Description'"
                :rules="[
                  validateRules.required(
                    websiteSettings.pageDescription,
                    'Page Description'
                  ),
                ]"
                placeholder="Enter page description"
                textarea
                rows="3"
                dense
                filled
                solo
                flat
                class="pt-0 mt-3"
                @input="$emit('settings-changed')"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Logo Section -->
      <v-card class="mb-6">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-image-filter-center-focus</v-icon>
          <span class="headline">Logo</span>
        </v-card-title>
        <v-card-text>
          <!-- Website Logo -->
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Website Logo</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-file-input
                v-model="websiteSettings.websiteLogo"
                accept="image/*"
                prepend-icon="mdi-image-outline"
                placeholder="Upload website logo"
                dense
                filled
                solo
                flat
                color="cyan"
                :show-size="1000"
                @change="handleWebsiteLogoUpload"
              ></v-file-input>
              <small class="text-grey"
                >Maximum file size: 3 MB (PNG, JPG, or SVG format)</small
              >
              <v-img
                v-if="websiteSettings.websiteLogoPreview"
                :src="websiteSettings.websiteLogoPreview"
                max-width="300"
                max-height="100"
                class="mt-2"
                contain
              ></v-img>
            </v-col>
          </v-row>

          <!-- Favicon -->
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Favicon</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-file-input
                v-model="websiteSettings.favicon"
                accept="image/*"
                prepend-icon="mdi-camera"
                placeholder="Upload favicon"
                dense
                filled
                solo
                flat
                color="cyan"
                :show-size="1000"
                @change="handleFaviconUpload"
              ></v-file-input>
              <small class="text-grey"
                >Maximum file size: 3 MB (PNG, JPG, or SVG format)
                </small
              >
              <v-img
                v-if="websiteSettings.faviconPreview"
                :src="websiteSettings.faviconPreview"
                max-width="32"
                max-height="32"
                class="mt-2"
              ></v-img>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Banner Images Section -->
      <v-card class="mb-6">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-image-multiple</v-icon>
          <span class="headline">Banner Images</span>
        </v-card-title>
        <v-card-text>
          <!-- Banner Image -->
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Banner Image</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-file-input
                v-model="websiteSettings.bannerImage"
                accept="image/*"
                prepend-icon="mdi-image"
                placeholder="Upload banner image"
                dense
                filled
                solo
                flat
                color="cyan"
                :show-size="1000"
                @change="handleBannerImageUpload"
              ></v-file-input>
              <small class="text-grey"
                >Maximum file size: 5 MB (JPG, PNG, or WebP format)</small
              >
              <v-img
                v-if="websiteSettings.bannerImagePreview"
                :src="websiteSettings.bannerImagePreview"
                max-width="300"
                max-height="100"
                class="mt-2"
                contain
              ></v-img>
            </v-col>
          </v-row>

          <!-- Booking Image -->
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Booking Image</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-file-input
                v-model="websiteSettings.bookingImage"
                accept="image/*"
                prepend-icon="mdi-calendar-check"
                placeholder="Upload booking image"
                dense
                filled
                solo
                flat
                color="cyan"
                :show-size="1000"
                @change="handleBookingImageUpload"
              ></v-file-input>
              <small class="text-grey"
                >Maximum file size: 5 MB (PNG, JPG, or SVG format)</small>
              <v-img
                v-if="websiteSettings.bookingImagePreview"
                :src="websiteSettings.bookingImagePreview"
                max-width="300"
                max-height="100"
                class="mt-2"
                contain
              ></v-img>
            </v-col>
          </v-row>

          <!-- Dashboard Image -->
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500"
                >Dashboard Image</label
              >
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-file-input
                v-model="websiteSettings.dashboardImage"
                accept="image/*"
                prepend-icon="mdi-view-dashboard"
                placeholder="Upload dashboard image"
                dense
                filled
                solo
                flat
                color="cyan"
                :show-size="1000"
                @change="handleDashboardImageUpload"
              ></v-file-input>
              <small class="text-grey"
                >Maximum file size: 5 MB (PNG, JPG, or SVG format)</small>
              <v-img
                v-if="websiteSettings.dashboardImagePreview"
                :src="websiteSettings.dashboardImagePreview"
                max-width="300"
                max-height="100"
                class="mt-2"
                contain
              ></v-img>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Appearance Section -->
      <v-card class="mb-6" v-if="false">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-palette</v-icon>
          <span class="headline">Appearance</span>
        </v-card-title>
        <v-card-text>
          <!-- Font Selector -->
          <v-row class="mb-4">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Font Family</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-select
                v-model="appearanceSettings.fontFamily"
                :items="fontOptions"
                item-text="label"
                item-value="value"
                placeholder="Select a font"
                dense
                filled
                solo
                flat
                color="cyan"
                hide-details
                :rules="[
                  validateRules.required(
                    appearanceSettings.fontFamily,
                    'Font Family'
                  ),
                ]"
                @change="$emit('settings-changed')"
              >
                <template v-slot:item="{ item }">
                  <div :style="{ fontFamily: item.value, padding: '8px 16px' }">
                    {{ item.label }}
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <span :style="{ fontFamily: item.value }">{{
                    item.label
                  }}</span>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Color Scheme with v-color-picker -->
          <v-row class="mb-4">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500">Color Scheme</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-menu
                v-model="colorPickerMenu"
                offset-y
                max-width="320"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="appearanceSettings.colorScheme"
                    v-bind="attrs"
                    v-on="on"
                    dense
                    filled
                    solo
                    flat
                    color="cyan"
                    placeholder="Select a color"
                    readonly
                    hide-details
                    :rules="[
                      validateRules.required(
                        appearanceSettings.colorScheme,
                        'Color Scheme'
                      ),
                    ]"
                  >
                    <template v-slot:prepend-inner>
                      <div
                        class="color-preview-box"
                        :style="{
                          backgroundColor: appearanceSettings.colorScheme,
                        }"
                      ></div>
                    </template>
                    <template v-slot:append>
                      <v-icon>mdi-chevron-down</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-card>
                  <v-card-text class="pa-2">
                    <v-color-picker
                      v-model="appearanceSettings.colorScheme"
                      dot-size="25"
                      swatches-max-height="200"
                      mode="hex"
                      hide-mode-switch
                      @update:color="onColorSchemeChange"
                    ></v-color-picker>
                  </v-card-text>
                  <v-card-actions class="justify-end pa-2">
                    <v-btn text small @click="colorPickerMenu = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Theme Background with v-color-picker -->
          <v-row class="mb-4">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500"
                >Theme Background</label
              >
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <v-menu
                v-model="themeColorPickerMenu"
                offset-y
                max-width="320"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="appearanceSettings.themeBackgroundColor"
                    v-bind="attrs"
                    v-on="on"
                    dense
                    filled
                    solo
                    flat
                    color="cyan"
                    placeholder="Select theme background color"
                    readonly
                    hide-details
                    :rules="[
                      validateRules.required(
                        appearanceSettings.themeBackgroundColor,
                        'Theme Background'
                      ),
                    ]"
                  >
                    <template v-slot:prepend-inner>
                      <div
                        class="color-preview-box"
                        :style="{
                          backgroundColor:
                            appearanceSettings.themeBackgroundColor,
                        }"
                      ></div>
                    </template>
                    <template v-slot:append>
                      <v-icon>mdi-chevron-down</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-card>
                  <v-card-text class="pa-2">
                    <v-color-picker
                      v-model="appearanceSettings.themeBackgroundColor"
                      dot-size="25"
                      swatches-max-height="200"
                      mode="hexa"
                      hide-mode-switch
                      @update:color="onThemeColorChange"
                    ></v-color-picker>
                  </v-card-text>
                  <v-card-actions class="justify-end pa-2">
                    <v-btn text small @click="themeColorPickerMenu = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Save Button -->
      <div class="d-flex justify-end">
        <v-btn
          color="success"
          :loading="saving"
          @click="saveSettings"
          :disabled="!formValid"
        >
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import validationMixin from "@/core/plugins/validation-mixin";

export default {
  name: "General",
  components: {},
  mixins: [validationMixin],
  props: {
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      loading: false,
      colorPickerMenu: false,
      themeColorPickerMenu: false,

      websiteSettings: {
        pageTitle: "",
        pageDescription: "",
        websiteLogo: null,
        websiteLogoPreview: "",
        favicon: null,
        faviconPreview: "",
        bannerImage: null,
        bannerImagePreview: "",
        bookingImage: null,
        bookingImagePreview: "",
        dashboardImage: null,
        dashboardImagePreview: "",
      },

      appearanceSettings: {
        colorScheme: "#1976D2",
        themeBackgroundColor: "#FFFFFF",
        fontFamily: "Roboto",
      },

      fontOptions: [
        { value: "Roboto", label: "Roboto" },
        { value: "Open Sans", label: "Open Sans" },
        { value: "Montserrat", label: "Montserrat" },
        { value: "Poppins", label: "Poppins" },
        { value: "Nunito", label: "Nunito" },
        { value: "Lato", label: "Lato" },
        { value: "Raleway", label: "Raleway" },
        { value: "Inter", label: "Inter" },
      ],

    };
  },

  methods: {
    async loadGeneralSettings() {
      try {
        this.loading = true;
        const response = await this.$http.get("/web-setup/general");

        if (response.data && response.data.success) {
          const data = response.data.data;

          // Load website settings
          if (data.website) {
            this.websiteSettings = {
              pageTitle: data.website.page_title || "",
              pageDescription: data.website.meta_description || "",
              websiteLogo: null,
              websiteLogoPreview: data.website.website_logo_url || "",
              favicon: null,
              faviconPreview: data.website.favicon_url || "",
              bannerImage: null,
              bannerImagePreview: data.website.banner_image_url || "",
              bookingImage: null,
              bookingImagePreview: data.website.booking_image_url || "",
              dashboardImage: null,
              dashboardImagePreview: data.website.dashboard_image_url || "",
            };
          }

          // Load appearance settings
          if (data.appearance) {
            this.appearanceSettings = {
              colorScheme: data.appearance.color_scheme || "#1976D2",
              themeBackgroundColor: data.appearance.theme_background_color || "#FFFFFF",
              fontFamily: data.appearance.font_family || "Roboto",
            };
          }
        } else {
          this.$emit(
            "show-error",
            response.data?.message || "Failed to load general settings"
          );
        }
      } catch (error) {
        this.$emit("show-error", "Failed to load general settings");
        console.error("Load general settings error:", error);
      } finally {
        this.loading = false;
      }
    },

    onColorSchemeChange(color) {
      this.appearanceSettings.colorScheme = color.hex || color;
      this.$emit("settings-changed");
    },

    onThemeColorChange(color) {
      this.appearanceSettings.themeBackgroundColor = color.hex || color;
      this.$emit("settings-changed");
    },

    handleWebsiteLogoUpload(file) {
      if (!file) {
        this.websiteSettings.websiteLogoPreview = "";
        this.$emit("settings-changed");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.websiteSettings.websiteLogoPreview = e.target.result;
        this.$emit("settings-changed");
      };
      reader.readAsDataURL(file);
    },

    handleFaviconUpload(file) {
      if (!file) {
        this.websiteSettings.faviconPreview = "";
        this.$emit("settings-changed");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.websiteSettings.faviconPreview = e.target.result;
        this.$emit("settings-changed");
      };
      reader.readAsDataURL(file);
    },

    handleBannerImageUpload(file) {
      if (!file) {
        this.websiteSettings.bannerImagePreview = "";
        this.$emit("settings-changed");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.websiteSettings.bannerImagePreview = e.target.result;
        this.$emit("settings-changed");
      };
      reader.readAsDataURL(file);
    },

    handleBookingImageUpload(file) {
      if (!file) {
        this.websiteSettings.bookingImagePreview = "";
        this.$emit("settings-changed");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.websiteSettings.bookingImagePreview = e.target.result;
        this.$emit("settings-changed");
      };
      reader.readAsDataURL(file);
    },

    handleDashboardImageUpload(file) {
      if (!file) {
        this.websiteSettings.dashboardImagePreview = "";
        this.$emit("settings-changed");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.websiteSettings.dashboardImagePreview = e.target.result;
        this.$emit("settings-changed");
      };
      reader.readAsDataURL(file);
    },

    async saveSettings() {
      if (!this.$refs.generalForm.validate()) {
        this.$emit("show-error", "Please fill all required fields correctly");
        return;
      }

      try {
        const settingsData = {
          website: { ...this.websiteSettings },
          appearance: { ...this.appearanceSettings },
        };

        this.$emit("save-settings", settingsData);
      } catch (error) {
        // this.$emit("show-error", "Failed to save settings");
        console.error("Save settings error:", error);
      }
    },

    getSettingsData() {
      return {
        website: { ...this.websiteSettings },
        appearance: { ...this.appearanceSettings },
      };
    },
  },

  async mounted() {
    await this.loadGeneralSettings();
  },
};
</script>

<style lang="scss" scoped>
.general-settings {
  padding: 20px;
}

.color-preview-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-right: 8px;
}
</style>